import React, { useRef, useEffect, useCallback, useState} from 'react';
import {useStepNumber} from './useStepNumber.js';
import Notes from 'midi/Notes.js';
import {WIDTH, LEFT_SPACE} from '../pianoroll/constants.js';
import {connect} from 'react-redux';
import {onMouseUp} from 'components/pianoroll/onMouseUp.js';
import {
    addStep,
    updateStepParameter,
    deleteSteps,
    moveSteps,
    snapToGrid
} from 'operations/actions.js';

export const useGridMouse = ({
    setSampleExplorerSelected,
    sequencerEngine,
    stepData,
    track,
    resolution,
    xScale,
    parentClassName,
    noteIndex,
    selectedPianoRollSteps,
    position,
    stepNumber,
    setPianoRollDragOffset,
    mouseDown,
    setDragStepTranspose,
    setStepDragging,
    setStepToCopy,
    setSelectedPianoRollSteps,
    setStepIsDraggingDuration,
    setStepDataDragging,
    setTransposeDragging,
    setPianoRollSelection,
    setSelectedFreeze,
    setIsDraggingDuration,
    setStepAndDataDragging
}) => { 
    let note = track && track.sequencerEngine.state.keyboard.notes[noteIndex] ||
        Notes.NOTES[0];

    const {getScrollLeft, getStepNumber} = useStepNumber({
        xScale, parentClassName});

    const _onMouseUp = useCallback((e) => {
        if (!track) { return; }

        e.stopPropagation();
        e.preventDefault();

        let state = sequencerEngine.store.getState();

        // first handle the "click" event, where we toggle a step on or off
        if (state.keyboard.isDraggingDuration) {
            // do nothing in this case
        } else if (hasMultipleSelected(state))  {
            startMultiDrag();
       } else if (state.keyboard.pianoRollSelection === undefined) {
           let isSelectedStep = state.keyboard.selectedPianoRollSteps !== undefined 
               && state.keyboard.selectedPianoRollSteps.indexOf(stepData) > -1;
            if (!isSelectedStep) {
                startSingleDrag();
            } else if (stepIsNotDragging(state, stepData)) {
                // when the step IS selected, and step is not dragging,
                // we delete
                if (!e.metaKey && !e.altKey) {
                    deleteStep(track, stepData);
                    setSelectedPianoRollSteps(undefined);
                }
            } else {
                snapToGridIfNecessary(track, resolution, e);
            }
        } 

        onMouseUp({
            mouseDown,
            setStepToCopy,
            setIsDraggingDuration,
            setStepDataDragging,
            setTransposeDragging,
            setSampleExplorerSelected
        });
    }, [stepData, track]);

    const getClickOffset = useCallback((e) => {
        let remaining = 62;
        let unquantizedOffset = position * WIDTH * xScale;
        let left = stepNumber * WIDTH * xScale + unquantizedOffset;
        return getScrollLeft() + e.pageX - left - remaining;
    }, [position, stepNumber, track]);

    const onMouseDown= useCallback((e, setStepNumber=true) => {
        if (!track) { return; }
        e.stopPropagation();
        e.preventDefault();

        // Keep track of where within the step user clicked
        setPianoRollDragOffset(getClickOffset(e));
        mouseDown(true);

        // keep track of what the transpose was on click
        setDragStepTranspose(note.getTranspose());

        if (setStepNumber) {
            let stepNumber = getStepNumber(e);
            setStepDragging(stepNumber);
        }

        // keep track of the step being operated on 
        setStepToCopy(stepData);
        setTransposeDragging(note.getTranspose());

        let selectedPianoRollSteps = track.sequencerEngine.state.keyboard.selectedPianoRollSteps;
        if (selectedPianoRollSteps === undefined ||
            !selectedPianoRollSteps.some(selected => selected && selected.id === stepData.id)) {
            // user clicked a step outside of the selection, so
            // reset the selection
            setSelectedPianoRollSteps([stepData]);
        }
    }, [note, getStepNumber, stepData, track]);

    const startMultiDrag = useCallback(() => {
        if (!track) { return; }
        // select notes
        let state = sequencerEngine.state;
        let selectedSteps = track.getEasyPattern()
            .getSelectedPianoRollSteps(
                state.keyboard.pianoRollSelection,
                resolution,
                true);
        
        setSelectedPianoRollSteps(selectedSteps);
        setSelectedFreeze(undefined);
        setPianoRollSelection(undefined);
    }, [track]);

    const startSingleDrag = useCallback(() => {
        // when the step is not selected, we need to select
        // that step
        let id = stepData.id;
        setSelectedPianoRollSteps([stepData]);
        setSelectedFreeze(undefined);
        setPianoRollSelection(undefined);
    }, [stepData, track]);

    const dragStepDuration = useCallback((e) => {
        if (!track) { return; }
        setIsDraggingDuration(true);
        setStepAndDataDragging(
            parseFloat(stepNumber) + parseFloat(position),
            track.sequencerEngine.state.keyboard.stepToCopy);
        onMouseDown(e, false);
    }, [stepNumber, position, track, stepData]);

    return {getStepNumber, onMouseUp: _onMouseUp, onMouseDown, dragStepDuration};
}

const deleteStep = (track, stepData) => {
    track.sequencerEngine.operationManager.dispatch(deleteSteps(
        track,
        [stepData]));
};

const snapToGridIfNecessary = (track, resolution, e) => {
    let state = track.sequencerEngine.state;
    if (!state.sequencer.quantized || e.metaKey || e.altKey) {
        // when in unquantized mode we don't need to snap
        // to grid

        // TODO: figure out how to do snap to grid in triplet
        // at the very least update the track steps thouggh...
        track.updateSteps();
        return;
    }
    let steps = state.keyboard.selectedPianoRollSteps;
    track.sequencerEngine.operationManager.dispatch(
        snapToGrid(
            track,
            steps,
            resolution));
};

const hasMultipleSelected = (state) => {
    return state.keyboard.pianoRollSelection !== undefined &&
        state.keyboard.pianoRollSelection.numNotes > 1 &&
        state.keyboard.pianoRollSelection.numNotes > 1;
};

const stepIsNotDragging = (state, stepData) => {
    return state.keyboard.stepDataDragging === undefined ||
        stepData.id !== state.keyboard.stepDataDragging.id;
}
